import { Box, Skeleton } from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { PostHogInsightResponse } from "../../../../utils/analytics/types";
import {
  formatAnalyticsNumber,
  parseBarBreakdown,
} from "../../../../utils/analytics/parsers";
import { analyticsTheme, BRAND, BRAND_DARK } from "../theme";
import { AnalyticsSectionCard } from "./AnalyticsSectionCard";
import { AnalyticsEmptyBlock } from "./AnalyticsEmptyBlock";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CHART_HEIGHT = 300;

interface DepartmentEngagementChartProps {
  title: string;
  response?: PostHogInsightResponse;
  isLoading?: boolean;
  emptyLabel: string;
}

export const DepartmentEngagementChart = ({
  title,
  response,
  isLoading,
  emptyLabel,
}: DepartmentEngagementChartProps) => {
  const points = parseBarBreakdown(response);

  const makeGradient = (ctx: CanvasRenderingContext2D) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, CHART_HEIGHT);
    gradient.addColorStop(0, BRAND);
    gradient.addColorStop(1, BRAND_DARK);
    return gradient;
  };

  const chartData = points
    ? {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: title,
            data: points.map((p) => p.value),
            backgroundColor: (context: { chart: ChartJS }) => {
              const { ctx } = context.chart;
              return makeGradient(ctx);
            },
            hoverBackgroundColor: BRAND_DARK,
            borderRadius: 8,
            maxBarThickness: 56,
            categoryPercentage: 0.7,
            barPercentage: 0.85,
          },
        ],
      }
    : null;

  return (
    <AnalyticsSectionCard
      title={title}
      icon={<BarChartRoundedIcon />}
      testId="analytics-department-engagement"
    >
      {isLoading ? (
        <Skeleton
          variant="rounded"
          height={CHART_HEIGHT}
          sx={{ borderRadius: analyticsTheme.radius.sm }}
        />
      ) : chartData ? (
        <Box sx={{ height: CHART_HEIGHT }}>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              layout: { padding: { top: 8 } },
              plugins: {
                legend: { display: false },
                tooltip: {
                  backgroundColor: analyticsTheme.text.primary,
                  padding: 12,
                  cornerRadius: 10,
                  titleFont: { size: 12, weight: 600 },
                  bodyFont: { size: 13, weight: 700 },
                  displayColors: false,
                  callbacks: {
                    label: (item) =>
                      formatAnalyticsNumber(
                        typeof item.raw === "number"
                          ? item.raw
                          : Number(item.raw),
                      ),
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  border: { display: false },
                  ticks: {
                    color: analyticsTheme.text.secondary,
                    font: { size: 12, weight: 500 },
                  },
                },
                y: {
                  beginAtZero: true,
                  border: { display: false },
                  grid: { color: analyticsTheme.border },
                  ticks: {
                    color: analyticsTheme.text.muted,
                    font: { size: 12 },
                    padding: 8,
                    maxTicksLimit: 6,
                    callback: (value) =>
                      formatAnalyticsNumber(
                        typeof value === "number" ? value : Number(value),
                      ),
                  },
                },
              },
            }}
          />
        </Box>
      ) : (
        <AnalyticsEmptyBlock label={emptyLabel} height={CHART_HEIGHT} />
      )}
    </AnalyticsSectionCard>
  );
};
